import { Box, Spinner, type SpinnerSize } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useMemo } from 'react';

interface LoadingSpinnerProps {
  size?: number | SpinnerSize;
  color?: 'primary' | 'secondary' | 'inherit';
  message?: string;
  showRandomMessage?: boolean;
}

function resolveSpinnerSize(size: number | SpinnerSize): SpinnerSize {
  if (typeof size === 'string') {
    return size;
  }
  if (size <= 16) {
    return 'sm';
  }
  if (size <= 24) {
    return 'md';
  }
  return 'lg';
}

export default function LoadingSpinner({
  size = 40,
  message,
  showRandomMessage = true,
}: Readonly<LoadingSpinnerProps>) {
  const { t } = useTranslation();

  const randomMessage = useMemo(() => {
    if (!showRandomMessage) {
      return null;
    }

    // Enumerated as literal-key t() calls (not a dynamic key) so the
    // i18next-parser statically extracts all 8 keys + inline defaults.
    // extractor-hint: dashboard.loading.message1 .. dashboard.loading.message8
    const messages = [
      t('dashboard.loading.message1', 'Brewing some fresh data...'),
      t(
        'dashboard.loading.message2',
        'Gathering insights from the digital realm...'
      ),
      t('dashboard.loading.message3', 'Assembling your dashboard with care...'),
      t('dashboard.loading.message4', 'Loading pixels and possibilities...'),
      t('dashboard.loading.message5', 'Connecting to the data matrix...'),
      t('dashboard.loading.message6', 'Preparing your workspace...'),
      t('dashboard.loading.message7', 'Fetching wisdom from the cloud...'),
      t('dashboard.loading.message8', 'Crafting your perfect view...'),
    ];

    // Rotate the message by mount time — variety is cosmetic, no RNG needed.
    return messages[Date.now() % messages.length];
  }, [t, showRandomMessage]);

  const displayMessage = message || randomMessage;

  return (
    <Box className="flex min-h-0 flex-grow flex-col items-center justify-center gap-2">
      <Spinner size={resolveSpinnerSize(size)} />
      {displayMessage && (
        <Box className="max-w-[300px] text-center text-xs text-muted-foreground">
          {displayMessage}
        </Box>
      )}
    </Box>
  );
}
